import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { format } from 'date-fns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore, useSubscriptionStore } from 'store';
import { useServerSearch } from '../../hooks/useServerSearch';
import DecimalField from '../shared/DecimalField';
import HelpButton from '../shared/HelpButton';
import WeekdayHoursEditor from '../shared/WeekdayHoursEditor';
import MessagePreviewDialog from '../shared/MessagePreviewDialog';
import { calcPriceExclVat, DEFAULT_VAT_RATE } from '../../utils/vat';

const WEEKDAYS = [
  { index: 0, key: 'mon', label: 'Ma' },
  { index: 1, key: 'tue', label: 'Ti' },
  { index: 2, key: 'wed', label: 'Ke' },
  { index: 3, key: 'thu', label: 'To' },
  { index: 4, key: 'fri', label: 'Pe' },
  { index: 5, key: 'sat', label: 'La' },
  { index: 6, key: 'sun', label: 'Su' },
];

const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const dateToTimeString = (date) => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const DEFAULT_AVAILABLE_HOURS = {
  mon: { open: '08:00', close: '16:00', closed: false },
  tue: { open: '08:00', close: '16:00', closed: false },
  wed: { open: '08:00', close: '16:00', closed: false },
  thu: { open: '08:00', close: '16:00', closed: false },
  fri: { open: '08:00', close: '16:00', closed: false },
  sat: { open: '08:00', close: '16:00', closed: true },
  sun: { open: '08:00', close: '16:00', closed: true },
};

const hasNonDefaultFormFields = (cfg) => {
  const defaultConfig = {
    vehicle_registration: { visible: true, required: true },
    customer_name: { visible: true, required: true },
    customer_phone: { visible: true, required: true },
    customer_email: { visible: true, required: false },
    customer_notes: { visible: true, required: false },
  };
  const fc = cfg.form_field_config || {};
  if (Object.keys(fc).length > 0) {
    for (const key of Object.keys(defaultConfig)) {
      const def = defaultConfig[key];
      const cur = fc[key] || {};
      if (cur.visible !== def.visible || cur.required !== def.required) return true;
    }
  }
  return (cfg.custom_form_fields || []).length > 0;
};

// ============================================================================
// SettingsAccordion
// ============================================================================

function SettingsAccordion({ id, expanded, onChange, title, badge, children }) {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', overflow: 'hidden' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">{title}</Typography>
          {badge}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Try to parse a plain numeric price from price_indication string.
 * Returns the number if it's a simple price like "45", "45.00", "45,00", "120".
 * Returns '' if it contains text like "alk.", "alkaen", ranges, or is non-numeric.
 */
const parsePriceIndication = (priceStr) => {
  if (!priceStr) return '';
  // Strip currency symbols and whitespace
  const cleaned = priceStr.replace(/[€\s]/g, '').replace(',', '.').trim();
  // Only accept if the entire string is a valid number
  if (/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return cleaned;
  }
  return '';
};

// ============================================================================
// Job Linking Autocomplete (shared by ServiceDialog and ExtraDialog)
// ============================================================================

function JobLinkAutocomplete({ value, onChange, nameForPrefill, priceIndicationForPrefill, initialJobName, initialJobPriceInclVat, helpKey, t }) {
  const {
    items: jobs,
    autocompleteProps: jobsAcProps,
    reset: resetJobsSearch,
  } = useServerSearch('/api/jobs/', { loadOnMount: true });

  const [creatingJob, setCreatingJob] = useState(false);
  const [newJobName, setNewJobName] = useState('');
  const [newJobPrice, setNewJobPrice] = useState('');
  const [savingJob, setSavingJob] = useState(false);

  // Find job in loaded list, or build a placeholder from parent data
  let selectedJob = value ? jobs.find((j) => j.id === value) || null : null;
  if (!selectedJob && value && initialJobName) {
    selectedJob = { id: value, name: initialJobName, price_incl_vat: initialJobPriceInclVat };
  }

  const getOptionLabel = (option) => {
    if (!option) return '';
    if (option._addNew) return option.label;
    let label = option.name;
    if (option.price_incl_vat != null) {
      label += ` (${parseFloat(option.price_incl_vat).toFixed(2)} €)`;
    }
    return label;
  };

  const handleStartCreate = () => {
    setNewJobName(nameForPrefill || '');
    setNewJobPrice(parsePriceIndication(priceIndicationForPrefill));
    setCreatingJob(true);
  };

  const handleCreateJob = async () => {
    if (!newJobName.trim()) return;
    setSavingJob(true);
    try {
      const priceInclVat = parseFloat(newJobPrice) || 0;
      const priceExclVat = priceInclVat > 0 ? calcPriceExclVat(priceInclVat, DEFAULT_VAT_RATE) : 0;
      const response = await axios.post('/api/jobs/', {
        name: newJobName.trim(),
        price: priceExclVat,
        price_type: 'fixed',
        vat_rate: DEFAULT_VAT_RATE,
        stored_price_incl_vat: priceInclVat > 0 ? Math.round(priceInclVat * 100) / 100 : null,
        price_entered_with_vat: priceInclVat > 0,
      });
      const createdJob = response.data;
      onChange(createdJob.id);
      resetJobsSearch();
      setCreatingJob(false);
    } catch (err) {
      console.error('Failed to create job:', err);
    } finally {
      setSavingJob(false);
    }
  };

  // Build options: real jobs + "add new" sentinel
  const options = [
    ...jobs,
    { _addNew: true, id: '__add_new__', label: `+ ${t('booking.services.add_new_job')}` },
  ];

  if (creatingJob) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
        <Typography variant="body2" fontWeight={600}>{t('booking.services.add_new_job')}</Typography>
        <TextField
          label={t('booking.services.new_job_name')}
          value={newJobName}
          onChange={(e) => setNewJobName(e.target.value)}
          size="small"
          fullWidth
          autoFocus
        />
        <DecimalField
          label={t('booking.services.new_job_price')}
          value={newJobPrice}
          onChange={(val) => setNewJobPrice(val)}
          size="small"
          fullWidth
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setCreatingJob(false)}>{t('Cancel')}</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleCreateJob}
            disabled={savingJob || !newJobName.trim()}
          >
            {savingJob ? <CircularProgress size={16} /> : t('Save')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        options={options}
        value={selectedJob}
        onChange={(e, newValue) => {
          if (newValue && newValue._addNew) {
            handleStartCreate();
          } else {
            onChange(newValue?.id || null);
          }
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        {...jobsAcProps}
        renderOption={(props, option) => {
          if (option._addNew) {
            return (
              <li {...props} key="__add_new__" style={{ fontWeight: 600, color: '#1976d2' }}>
                {option.label}
              </li>
            );
          }
          return <li {...props} key={option.id}>{getOptionLabel(option)}</li>;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            label={t('booking.services.linked_job')}
            placeholder={t('booking.services.linked_job_placeholder')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {jobsAcProps.loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {t(helpKey || 'booking.services.linked_job_help')}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Service Category Dialog
// ============================================================================

function ServiceCategoryDialog({ open, onClose, onSave, category, t }) {
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setForm({
        name: category.name || '',
        description: category.description || '',
      });
    } else {
      setForm({ name: '', description: '' });
    }
  }, [category, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, category?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save category:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {category ? t('booking.categories.edit') : t('booking.categories.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('booking.categories.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label={t('booking.categories.description')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.name}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Service Dialog
// ============================================================================

function ServiceDialog({ open, onClose, onSave, service, calendarAware, availableEmployees, availableBays, categories, t }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    price_indication: '',
    category: null,
    booking_mode: 'time_slot',
    is_active: true,
    employees: [],
    service_bays: [],
    job: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (service) {
      setForm({
        name: service.name || '',
        description: service.description || '',
        duration_minutes: service.duration_minutes || 60,
        price_indication: service.price_indication || '',
        category: service.category || null,
        booking_mode: service.booking_mode || 'time_slot',
        is_active: service.is_active ?? true,
        employees: service.employees || [],
        service_bays: service.service_bays || [],
        job: service.job || null,
      });
    } else {
      setForm({
        name: '',
        description: '',
        duration_minutes: 60,
        price_indication: '',
        category: null,
        booking_mode: 'time_slot',
        is_active: true,
        employees: [],
        service_bays: [],
        job: null,
      });
    }
  }, [service, open]);

  const toggleEmployee = (empId) => {
    setForm((prev) => {
      const emps = [...prev.employees];
      const idx = emps.indexOf(empId);
      if (idx >= 0) emps.splice(idx, 1);
      else emps.push(empId);
      return { ...prev, employees: emps };
    });
  };

  const toggleBay = (bayId) => {
    setForm((prev) => {
      const bays = [...prev.service_bays];
      const idx = bays.indexOf(bayId);
      if (idx >= 0) bays.splice(idx, 1);
      else bays.push(bayId);
      return { ...prev, service_bays: bays };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, service?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save service:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {service ? t('booking.services.edit') : t('booking.services.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('booking.services.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label={t('booking.services.description')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={t('booking.services.duration')}
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('booking.services.price')}
                value={form.price_indication}
                onChange={(e) => setForm({ ...form, price_indication: e.target.value })}
                fullWidth
                placeholder="alk. 49€"
              />
            </Grid>
          </Grid>
          <JobLinkAutocomplete
            value={form.job}
            onChange={(jobId) => setForm({ ...form, job: jobId })}
            nameForPrefill={form.name}
            priceIndicationForPrefill={form.price_indication}
            initialJobName={service?.job_name}
            initialJobPriceInclVat={service?.job_price_incl_vat}
            t={t}
          />
          <FormControl fullWidth>
            <InputLabel>{t('booking.services.category')}</InputLabel>
            <Select
              value={form.category || ''}
              label={t('booking.services.category')}
              onChange={(e) => setForm({ ...form, category: e.target.value || null })}
            >
              <MenuItem value="">{t('booking.services.no_category')}</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t('booking.services.mode')}</InputLabel>
            <Select
              value={form.booking_mode}
              label={t('booking.services.mode')}
              onChange={(e) => setForm({ ...form, booking_mode: e.target.value })}
            >
              <MenuItem value="time_slot">{t('booking.services.mode_time_slot')}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label={t('booking.services.active')}
          />
          {calendarAware && (
            <>
              {availableEmployees && availableEmployees.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('booking.services.employees')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {t('booking.services.employees_help')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {availableEmployees.map((emp) => (
                      <Chip
                        key={emp.id}
                        label={emp.name}
                        onClick={() => toggleEmployee(emp.id)}
                        color={form.employees.includes(emp.id) ? 'primary' : 'default'}
                        variant={form.employees.includes(emp.id) ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {availableBays && availableBays.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t('booking.services.service_bays')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {t('booking.services.service_bays_help')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {availableBays.map((bay) => (
                      <Chip
                        key={bay.id}
                        label={bay.name}
                        onClick={() => toggleBay(bay.id)}
                        color={form.service_bays.includes(bay.id) ? 'primary' : 'default'}
                        variant={form.service_bays.includes(bay.id) ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.name}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Drop-off Window Dialog
// ============================================================================

function DropOffWindowDialog({ open, onClose, onSave, window: windowData, t }) {
  const [form, setForm] = useState({
    name: '',
    start_time: '07:00',
    end_time: '09:00',
    daily_capacity: 15,
    available_days: [0, 1, 2, 3, 4],
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (windowData) {
      setForm({
        name: windowData.name || '',
        start_time: windowData.start_time || '07:00',
        end_time: windowData.end_time || '09:00',
        daily_capacity: windowData.daily_capacity || 15,
        available_days: windowData.available_days || [0, 1, 2, 3, 4],
        is_active: windowData.is_active ?? true,
      });
    } else {
      setForm({
        name: '',
        start_time: '07:00',
        end_time: '09:00',
        daily_capacity: 15,
        available_days: [0, 1, 2, 3, 4],
        is_active: true,
      });
    }
  }, [windowData, open]);

  const toggleDay = (dayIndex) => {
    setForm((prev) => {
      const days = [...prev.available_days];
      const idx = days.indexOf(dayIndex);
      if (idx >= 0) {
        days.splice(idx, 1);
      } else {
        days.push(dayIndex);
        days.sort((a, b) => a - b);
      }
      return { ...prev, available_days: days };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, windowData?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save drop-off window:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {windowData ? t('booking.drop_off.edit') : t('booking.drop_off.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('booking.drop_off.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            fullWidth
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TimePicker
                  label={t('booking.drop_off.start_time')}
                  value={timeStringToDate(form.start_time)}
                  onChange={(v) => setForm({ ...form, start_time: dateToTimeString(v) })}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label={t('booking.drop_off.end_time')}
                  value={timeStringToDate(form.end_time)}
                  onChange={(v) => setForm({ ...form, end_time: dateToTimeString(v) })}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <TextField
            label={t('booking.drop_off.capacity')}
            type="number"
            value={form.daily_capacity}
            onChange={(e) => setForm({ ...form, daily_capacity: parseInt(e.target.value) || 0 })}
            fullWidth
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('booking.drop_off.available_days')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {WEEKDAYS.map((day) => (
                <Chip
                  key={day.index}
                  label={day.label}
                  onClick={() => toggleDay(day.index)}
                  color={form.available_days.includes(day.index) ? 'primary' : 'default'}
                  variant={form.available_days.includes(day.index) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label={t('booking.services.active')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.name}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Unavailability Period Dialog
// ============================================================================

function UnavailabilityDialog({ open, onClose, onSave, period, t }) {
  const [form, setForm] = useState({
    start_datetime: null,
    end_datetime: null,
    reason: '',
    show_in_calendar: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (period) {
      setForm({
        start_datetime: period.start_datetime ? new Date(period.start_datetime) : null,
        end_datetime: period.end_datetime ? new Date(period.end_datetime) : null,
        reason: period.reason || '',
        show_in_calendar: period.show_in_calendar ?? false,
      });
    } else {
      setForm({
        start_datetime: null,
        end_datetime: null,
        reason: '',
        show_in_calendar: false,
      });
    }
  }, [period, open]);

  const handleSave = async () => {
    if (!form.start_datetime || !form.end_datetime) return;
    if (form.start_datetime >= form.end_datetime) return;
    setSaving(true);
    try {
      await onSave(form, period?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save unavailability period:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {period ? t('booking.config.unavailability_edit') : t('booking.config.unavailability_add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DateTimePicker
              label={t('booking.config.unavailability_start')}
              value={form.start_datetime}
              onChange={(v) => setForm({ ...form, start_datetime: v })}
              ampm={false}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DateTimePicker
              label={t('booking.config.unavailability_end')}
              value={form.end_datetime}
              onChange={(v) => setForm({ ...form, end_datetime: v })}
              ampm={false}
              referenceDate={form.start_datetime || undefined}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          <TextField
            label={t('booking.config.unavailability_reason')}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.show_in_calendar}
                onChange={(e) => setForm({ ...form, show_in_calendar: e.target.checked })}
              />
            }
            label={t('booking.config.unavailability_show_in_calendar')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.start_datetime || !form.end_datetime}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Break Period Dialog
// ============================================================================

function BreakPeriodDialog({ open, onClose, onSave, breakPeriod, t }) {
  const [form, setForm] = useState({
    name: '',
    start_time: '12:00',
    end_time: '12:30',
    days: [0, 1, 2, 3, 4, 5, 6],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (breakPeriod) {
      setForm({
        name: breakPeriod.name || '',
        start_time: breakPeriod.start_time || '12:00',
        end_time: breakPeriod.end_time || '12:30',
        days: breakPeriod.days?.length > 0 ? breakPeriod.days : [0, 1, 2, 3, 4, 5, 6],
      });
    } else {
      setForm({
        name: '',
        start_time: '12:00',
        end_time: '12:30',
        days: [0, 1, 2, 3, 4, 5, 6],
      });
    }
  }, [breakPeriod, open]);

  const toggleDay = (dayIndex) => {
    setForm((prev) => {
      const days = [...prev.days];
      const idx = days.indexOf(dayIndex);
      if (idx >= 0) {
        days.splice(idx, 1);
      } else {
        days.push(dayIndex);
        days.sort((a, b) => a - b);
      }
      return { ...prev, days };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Empty list = all days; if all 7 selected, send empty to mean "every day"
        days: form.days.length === 7 ? [] : form.days,
      };
      await onSave(payload, breakPeriod?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save break period:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {breakPeriod ? t('booking.breaks.edit') : t('booking.breaks.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('booking.breaks.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            placeholder="esim. Lounastauko"
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TimePicker
                  label={t('booking.breaks.start_time')}
                  value={timeStringToDate(form.start_time)}
                  onChange={(v) => setForm({ ...form, start_time: dateToTimeString(v) })}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label={t('booking.breaks.end_time')}
                  value={timeStringToDate(form.end_time)}
                  onChange={(v) => setForm({ ...form, end_time: dateToTimeString(v) })}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('booking.breaks.days')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {WEEKDAYS.map((day) => (
                <Chip
                  key={day.index}
                  label={day.label}
                  onClick={() => toggleDay(day.index)}
                  color={form.days.includes(day.index) ? 'primary' : 'default'}
                  variant={form.days.includes(day.index) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.start_time || !form.end_time}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Extra Service Dialog
// ============================================================================

function ExtraDialog({ open, onClose, onSave, extra, services, t }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    price_indication: '',
    duration_minutes: '',
    is_active: true,
    services: [],
    job: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (extra) {
      setForm({
        name: extra.name || '',
        description: extra.description || '',
        price_indication: extra.price_indication || '',
        duration_minutes: extra.duration_minutes ?? '',
        is_active: extra.is_active ?? true,
        services: extra.services || [],
        job: extra.job || null,
      });
    } else {
      setForm({
        name: '',
        description: '',
        price_indication: '',
        duration_minutes: '',
        is_active: true,
        services: [],
        job: null,
      });
    }
  }, [extra, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_minutes: form.duration_minutes !== '' ? parseInt(form.duration_minutes, 10) : null,
      };
      await onSave(payload, extra?.id);
      onClose();
    } catch (err) {
      console.error('Failed to save extra:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (serviceId) => {
    setForm((prev) => {
      const svcs = [...prev.services];
      const idx = svcs.indexOf(serviceId);
      if (idx >= 0) {
        svcs.splice(idx, 1);
      } else {
        svcs.push(serviceId);
      }
      return { ...prev, services: svcs };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {extra ? t('booking.extras.edit') : t('booking.extras.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('booking.extras.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label={t('booking.extras.description')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label={t('booking.extras.price')}
            value={form.price_indication}
            onChange={(e) => setForm({ ...form, price_indication: e.target.value })}
            fullWidth
            placeholder="15€"
          />
          <TextField
            label={t('booking.extras.duration')}
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            fullWidth
            placeholder="15"
            type="number"
            inputProps={{ min: 0 }}
            helperText={t('booking.extras.duration_help')}
          />
          <JobLinkAutocomplete
            value={form.job}
            onChange={(jobId) => setForm({ ...form, job: jobId })}
            nameForPrefill={form.name}
            priceIndicationForPrefill={form.price_indication}
            initialJobName={extra?.job_name}
            initialJobPriceInclVat={extra?.job_price_incl_vat}
            helpKey="booking.extras.linked_job_help"
            t={t}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('booking.extras.select_services')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {services.map((svc) => (
                <Chip
                  key={svc.id}
                  label={svc.name}
                  onClick={() => toggleService(svc.id)}
                  color={form.services.includes(svc.id) ? 'primary' : 'default'}
                  variant={form.services.includes(svc.id) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label={t('booking.extras.active')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !form.name}
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Custom Field Dialog
// ============================================================================

function CustomFieldDialog({ open, onClose, onSave, field, t }) {
  const [form, setForm] = useState({ label: '', type: 'text', required: false, options: [] });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (field) {
      setForm({ label: field.label, type: field.type, required: field.required || false, options: field.options || [] });
    } else {
      setForm({ label: '', type: 'text', required: false, options: [] });
    }
    setNewOption('');
  }, [field, open]);

  const handleSave = () => {
    if (!form.label.trim()) return;
    onSave({
      id: field?.id || crypto.randomUUID(),
      ...form,
      label: form.label.trim(),
    });
    onClose();
  };

  const addOption = () => {
    if (newOption.trim()) {
      setForm({ ...form, options: [...form.options, newOption.trim()] });
      setNewOption('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{field ? t('booking.config.edit_custom_field') : t('booking.config.add_custom_field')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label={t('booking.config.field_label')} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>{t('booking.config.field_type')}</InputLabel>
            <Select value={form.type} label={t('booking.config.field_type')} onChange={(e) => setForm({ ...form, type: e.target.value, options: e.target.value === 'select' ? form.options : [] })}>
              <MenuItem value="text">{t('booking.config.field_type_text')}</MenuItem>
              <MenuItem value="textarea">{t('booking.config.field_type_textarea')}</MenuItem>
              <MenuItem value="select">{t('booking.config.field_type_select')}</MenuItem>
              <MenuItem value="checkbox">{t('booking.config.field_type_checkbox')}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />}
            label={t('booking.config.field_required')}
          />
          {form.type === 'select' && (
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t('booking.config.field_options')}</Typography>
              {form.options.map((opt, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{opt}</Typography>
                  <IconButton size="small" onClick={() => setForm({ ...form, options: form.options.filter((_, i) => i !== idx) })}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField size="small" value={newOption} onChange={(e) => setNewOption(e.target.value)} placeholder={t('booking.config.add_option')} fullWidth
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                />
                <Button size="small" onClick={addOption} variant="outlined">{t('Add')}</Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!form.label.trim()}>{t('Save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// Main BookingSettingsTab
// ============================================================================

export default function BookingSettingsTab({ onDirtyChange }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfigRaw] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isConfigDirty, setIsConfigDirty] = useState(false);
  const [services, setServices] = useState([]);
  const [extras, setExtras] = useState([]);
  const [dropOffWindows, setDropOffWindows] = useState([]);
  const [orgEmployees, setOrgEmployees] = useState([]);
  const [orgServiceBays, setOrgServiceBays] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [embedExpanded, setEmbedExpanded] = useState(false);
  const colorInputRefs = useRef({});

  // Accordion expanded state
  const [expanded, setExpanded] = useState({});

  // Service dialog
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteServiceId, setDeleteServiceId] = useState(null);

  // Category dialog
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);

  // Extra dialog
  const [extraDialogOpen, setExtraDialogOpen] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [deleteExtraId, setDeleteExtraId] = useState(null);

  // Drop-off window dialog
  const [windowDialogOpen, setWindowDialogOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  const [deleteWindowId, setDeleteWindowId] = useState(null);

  // Break period dialog
  const [breakPeriods, setBreakPeriods] = useState([]);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [deleteBreakId, setDeleteBreakId] = useState(null);

  // Custom field dialog
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);

  // Unavailability periods
  const [unavailabilityPeriods, setUnavailabilityPeriods] = useState([]);
  const [unavailabilityDialogOpen, setUnavailabilityDialogOpen] = useState(false);
  const [editingUnavailability, setEditingUnavailability] = useState(null);
  const [deleteUnavailabilityId, setDeleteUnavailabilityId] = useState(null);

  const setConfig = useCallback((updater) => {
    setConfigRaw(updater);
    setIsConfigDirty(true);
  }, []);

  const hasDropOffServices = services.some((s) => s.booking_mode === 'drop_off');

  // After data loads, compute initial expanded state
  useEffect(() => {
    if (config) {
      setExpanded({
        hours: true,
        categories: categories.length > 0,
        services: true,
        extras: extras.length > 0,
        dropoff: dropOffWindows.length > 0,
        capacity: config.concurrent_slots !== 2 || config.buffer_minutes !== 5 || config.max_advance_days !== 30 || config.min_advance_hours !== 2 || config.calendar_aware,
        reminders: config.reminder_hours_before !== 24 || !config.reminder_confirm_enabled || !!config.notification_sms_number || !!config.notification_email,
        appearance: !!config.page_bg_color || !!config.card_bg_color || !!config.text_color || !!config.button_color,
        texts: !!config.custom_welcome_text || !!config.custom_confirmation_text,
        formFields: hasNonDefaultFormFields(config),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.id]);

  const toggleAccordion = (key) => () => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ===== Data fetching =====

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-config/');
      const data = response.data;
      // Ensure available_hours has proper structure
      if (!data.available_hours || Object.keys(data.available_hours).length === 0) {
        data.available_hours = { ...DEFAULT_AVAILABLE_HOURS };
      }
      setConfigRaw(data);
      setIsConfigDirty(false);
    } catch (err) {
      console.error('Failed to fetch booking config:', err);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-services/');
      setServices(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-categories/', { params: { organization: currentOrganization?.organization_id } });
      setCategories(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [currentOrganization?.organization_id]);

  const fetchExtras = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-extras/');
      setExtras(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch extras:', err);
    }
  }, []);

  const fetchDropOffWindows = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-drop-off-windows/');
      setDropOffWindows(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch drop-off windows:', err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get('/api/employees/');
      setOrgEmployees((response.data.results || response.data).filter((e) => e.state === 'active'));
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  }, []);

  const fetchServiceBays = useCallback(async () => {
    try {
      const response = await axios.get('/api/scheduling/service-bays/');
      setOrgServiceBays((response.data.results || response.data).filter((b) => b.is_active));
    } catch (err) {
      console.error('Failed to fetch service bays:', err);
    }
  }, []);

  const fetchUnavailabilityPeriods = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-unavailability-periods/');
      setUnavailabilityPeriods(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch unavailability periods:', err);
    }
  }, []);

  const fetchBreakPeriods = useCallback(async () => {
    try {
      const response = await axios.get('/api/booking-break-periods/');
      setBreakPeriods(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch break periods:', err);
    }
  }, []);

  useEffect(() => {
    if (!subscriptionInfo) {
      fetchSubscriptionInfo();
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchServices(), fetchCategories(), fetchExtras(), fetchDropOffWindows(), fetchEmployees(), fetchServiceBays(), fetchUnavailabilityPeriods(), fetchBreakPeriods()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchConfig, fetchServices, fetchCategories, fetchExtras, fetchDropOffWindows, fetchEmployees, fetchServiceBays, fetchUnavailabilityPeriods, fetchBreakPeriods]);

  // ===== Config save =====

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.patch('/api/booking-config/', config);
      setConfigRaw(response.data);
      setIsConfigDirty(false);
      setSnackbar({ open: true, message: t('booking.config.saved'), severity: 'success' });
    } catch (err) {
      console.error('Failed to save config:', err);
      // Extract meaningful error message from DRF response (field-level or detail)
      let errorMsg = t('booking.config.save_error');
      const data = err.response?.data;
      if (data) {
        if (data.detail) {
          errorMsg = data.detail;
        } else if (typeof data === 'object') {
          // DRF field-level errors: { field: ["msg"] } or { field: "msg" }
          const messages = Object.values(data)
            .flat()
            .filter(Boolean);
          if (messages.length > 0) {
            errorMsg = messages.join(', ');
          }
        }
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasConfigChanges = isConfigDirty;

  const handleSaveConfigRef = useRef(handleSaveConfig);
  handleSaveConfigRef.current = handleSaveConfig;

  useEffect(() => {
    onDirtyChange?.(hasConfigChanges, () => handleSaveConfigRef.current());
  }, [hasConfigChanges, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Workshop type seeding =====

  const handleSeedDefaults = async (workshopType) => {
    try {
      await axios.patch('/api/booking-config/', { workshop_type: workshopType });
      await fetchServices();
      setSnackbar({ open: true, message: t('booking.config.defaults_seeded'), severity: 'success' });
    } catch (err) {
      console.error('Failed to seed defaults:', err);
    }
  };

  // ===== Service CRUD =====

  const handleSaveService = async (formData, serviceId) => {
    if (serviceId) {
      await axios.patch(`/api/booking-services/${serviceId}/`, formData);
    } else {
      await axios.post('/api/booking-services/', formData);
    }
    await fetchServices();
    setSnackbar({ open: true, message: t('booking.services.saved'), severity: 'success' });
  };

  const handleDeleteService = async () => {
    if (!deleteServiceId) return;
    try {
      await axios.delete(`/api/booking-services/${deleteServiceId}/`);
      await fetchServices();
      setDeleteServiceId(null);
      setSnackbar({ open: true, message: t('booking.services.deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete service:', err);
    }
  };

  const handleMoveService = async (service, direction) => {
    const sorted = [...services].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const idx = sorted.findIndex((s) => s.id === service.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Swap positions in the array, then assign sequential sort_order values
    const reordered = [...sorted];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];

    try {
      const updates = reordered
        .map((s, i) => ({ id: s.id, oldOrder: s.sort_order, newOrder: i }))
        .filter((u) => u.oldOrder !== u.newOrder);
      await Promise.all(updates.map((u) => axios.patch(`/api/booking-services/${u.id}/`, { sort_order: u.newOrder })));
      await fetchServices();
    } catch (err) {
      console.error('Failed to reorder services:', err);
    }
  };

  // ===== Category CRUD =====

  const handleSaveCategory = async (formData, categoryId) => {
    if (categoryId) {
      await axios.patch(`/api/booking-categories/${categoryId}/`, formData, { params: { organization: currentOrganization?.organization_id } });
    } else {
      await axios.post('/api/booking-categories/', { ...formData, organization: currentOrganization?.organization_id }, { params: { organization: currentOrganization?.organization_id } });
    }
    await fetchCategories();
    setSnackbar({ open: true, message: t('booking.categories.saved'), severity: 'success' });
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryId) return;
    try {
      await axios.delete(`/api/booking-categories/${deleteCategoryId}/`, { params: { organization: currentOrganization?.organization_id } });
      setDeleteCategoryId(null);
      await fetchCategories();
      await fetchServices();
      setSnackbar({ open: true, message: t('booking.categories.deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete category:', err);
    }
  };

  const handleMoveCategory = async (category, direction) => {
    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const idx = sorted.findIndex((c) => c.id === category.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Swap positions in the array, then assign sequential sort_order values
    const reordered = [...sorted];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];

    try {
      const updates = reordered
        .map((c, i) => ({ id: c.id, oldOrder: c.sort_order, newOrder: i }))
        .filter((u) => u.oldOrder !== u.newOrder);
      await Promise.all(updates.map((u) => axios.patch(`/api/booking-categories/${u.id}/`, { sort_order: u.newOrder }, { params: { organization: currentOrganization?.organization_id } })));
      await fetchCategories();
    } catch (err) {
      console.error('Failed to reorder categories:', err);
    }
  };

  // ===== Extra CRUD =====

  const handleSaveExtra = async (formData, extraId) => {
    if (extraId) {
      await axios.patch(`/api/booking-extras/${extraId}/`, formData);
    } else {
      await axios.post('/api/booking-extras/', formData);
    }
    await fetchExtras();
    setSnackbar({ open: true, message: t('booking.extras.saved'), severity: 'success' });
  };

  const handleDeleteExtra = async () => {
    if (!deleteExtraId) return;
    try {
      await axios.delete(`/api/booking-extras/${deleteExtraId}/`);
      await fetchExtras();
      setDeleteExtraId(null);
      setSnackbar({ open: true, message: t('booking.extras.deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete extra:', err);
    }
  };

  // ===== Drop-off Window CRUD =====

  const handleSaveWindow = async (formData, windowId) => {
    if (windowId) {
      await axios.patch(`/api/booking-drop-off-windows/${windowId}/`, formData);
    } else {
      await axios.post('/api/booking-drop-off-windows/', formData);
    }
    await fetchDropOffWindows();
    setSnackbar({ open: true, message: t('booking.drop_off.saved'), severity: 'success' });
  };

  const handleDeleteWindow = async () => {
    if (!deleteWindowId) return;
    try {
      await axios.delete(`/api/booking-drop-off-windows/${deleteWindowId}/`);
      await fetchDropOffWindows();
      setDeleteWindowId(null);
      setSnackbar({ open: true, message: t('booking.drop_off.deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete drop-off window:', err);
    }
  };

  // ===== Break Period CRUD =====

  const handleSaveBreak = async (formData, breakId) => {
    if (breakId) {
      await axios.patch(`/api/booking-break-periods/${breakId}/`, formData);
    } else {
      await axios.post('/api/booking-break-periods/', formData);
    }
    await fetchBreakPeriods();
    setSnackbar({ open: true, message: t('booking.breaks.saved'), severity: 'success' });
  };

  const handleDeleteBreak = async () => {
    if (!deleteBreakId) return;
    try {
      await axios.delete(`/api/booking-break-periods/${deleteBreakId}/`);
      await fetchBreakPeriods();
      setDeleteBreakId(null);
      setSnackbar({ open: true, message: t('booking.breaks.deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete break period:', err);
    }
  };

  // ===== Unavailability Period CRUD =====

  const handleSaveUnavailability = async (formData, periodId) => {
    const payload = {
      start_datetime: formData.start_datetime instanceof Date ? formData.start_datetime.toISOString() : formData.start_datetime,
      end_datetime: formData.end_datetime instanceof Date ? formData.end_datetime.toISOString() : formData.end_datetime,
      reason: formData.reason,
      show_in_calendar: formData.show_in_calendar,
    };
    if (periodId) {
      await axios.patch(`/api/booking-unavailability-periods/${periodId}/`, payload);
    } else {
      await axios.post('/api/booking-unavailability-periods/', payload);
    }
    await fetchUnavailabilityPeriods();
    setSnackbar({ open: true, message: t('booking.config.unavailability_added'), severity: 'success' });
  };

  const handleDeleteUnavailability = async () => {
    if (!deleteUnavailabilityId) return;
    try {
      await axios.delete(`/api/booking-unavailability-periods/${deleteUnavailabilityId}/`);
      await fetchUnavailabilityPeriods();
      setDeleteUnavailabilityId(null);
      setSnackbar({ open: true, message: t('booking.config.unavailability_deleted'), severity: 'success' });
    } catch (err) {
      console.error('Failed to delete unavailability period:', err);
    }
  };

  const handleToggleCalendarNote = async (period) => {
    try {
      const newValue = !period.calendar_note_id;
      await axios.patch(`/api/booking-unavailability-periods/${period.id}/`, {
        show_in_calendar: newValue,
      });
      await fetchUnavailabilityPeriods();
      setSnackbar({
        open: true,
        message: newValue
          ? t('booking.config.unavailability_added_to_calendar')
          : t('booking.config.unavailability_removed_from_calendar'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to toggle calendar note:', err);
    }
  };

  // ===== Custom Field CRUD =====

  const handleSaveCustomField = (fieldData) => {
    const existing = config.custom_form_fields || [];
    const idx = existing.findIndex(f => f.id === fieldData.id);
    let updated;
    if (idx >= 0) {
      updated = [...existing];
      updated[idx] = fieldData;
    } else {
      updated = [...existing, fieldData];
    }
    setConfig({ ...config, custom_form_fields: updated });
  };

  // ===== Copy URL =====

  const handleCopyUrl = async () => {
    const url = `https://varaa.verstaani.app/${config.slug}`;
    try {
      // writeText rejects when the document isn't focused (VERSTAANI-FRONTEND-J);
      // show the URL to copy manually instead of throwing an unhandled rejection.
      await navigator.clipboard.writeText(url);
      setSnackbar({ open: true, message: t('booking.config.url_copied'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: t('booking.config.url_copy_failed', { url }), severity: 'warning' });
    }
  };

  // ===== Loading state =====

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config) return null;

  const bookingUrl = `https://varaa.verstaani.app/${config.slug}`;
  const embedCode = `<iframe src="${bookingUrl}" width="100%" height="800" frameborder="0"></iframe>`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ===== Enable/Disable Section (flat Paper, no accordion) ===== */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {t('booking.config.enabled')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('booking.config.enabled_help')}
            </Typography>
          </Box>
          <Switch
            checked={config.is_enabled}
            onChange={(e) => setConfig({ ...config, is_enabled: e.target.checked })}
            color="primary"
          />
        </Box>

        {config.is_enabled && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('booking.config.shareable_url')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={bookingUrl}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Tooltip title={t('booking.config.copy_url')}>
                <IconButton onClick={handleCopyUrl} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() => setEmbedExpanded(!embedExpanded)}
                endIcon={embedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {t('booking.config.embed_code')}
              </Button>
              <Collapse in={embedExpanded}>
                <TextField
                  value={embedCode}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />
              </Collapse>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ===== Workshop Type + Initial Setup ===== */}
      {services.length === 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
            {t('booking.config.workshop_type')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => handleSeedDefaults('tire_shop')}
            >
              {t('booking.config.tire_shop')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSeedDefaults('general_workshop')}
            >
              {t('booking.config.general_workshop')}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('booking.config.seed_defaults')}
          </Typography>
        </Paper>
      )}

      {/* ===== Confirmation Mode (flat Paper, no accordion) ===== */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.confirmation_mode')}
        </Typography>
        <RadioGroup
          value={config.confirmation_mode}
          onChange={(e) => setConfig({ ...config, confirmation_mode: e.target.value })}
        >
          <FormControlLabel
            value="instant"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">{t('booking.config.mode_instant')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('booking.config.mode_instant_help')}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="manual"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">{t('booking.config.mode_manual')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('booking.config.mode_manual_help')}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </Paper>

      {/* ===== Available Hours Accordion ===== */}
      <SettingsAccordion
        id="hours"
        expanded={!!expanded.hours}
        onChange={toggleAccordion('hours')}
        title={t('booking.config.available_hours')}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.available_hours_help')}
        </Typography>
        <WeekdayHoursEditor
          value={config.available_hours}
          onChange={(availableHours) => setConfig((prev) => ({ ...prev, available_hours: availableHours }))}
          closedLabel={t('booking.config.closed')}
          testIdPrefix="booking-hours"
        />

        {/* ===== Unavailability Periods Subsection ===== */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
          {t('booking.config.unavailability_periods')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.unavailability_periods_help')}
        </Typography>

        {unavailabilityPeriods.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {unavailabilityPeriods.map((period) => (
              <Box
                key={period.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" fontWeight={500} sx={{ minWidth: 'fit-content' }}>
                  {format(new Date(period.start_datetime), 'd.M.yyyy HH:mm', { locale: fi })}
                  {' – '}
                  {format(new Date(period.end_datetime), 'd.M.yyyy HH:mm', { locale: fi })}
                </Typography>
                {period.reason && (
                  <Typography variant="body2" color="text.secondary">
                    ({period.reason})
                  </Typography>
                )}
                {period.calendar_note_id && (
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                    label={t('booking.config.unavailability_in_calendar')}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title={period.calendar_note_id ? t('booking.config.unavailability_remove_from_calendar') : t('booking.config.unavailability_add_to_calendar')}>
                  <IconButton size="small" onClick={() => handleToggleCalendarNote(period)}>
                    <CalendarTodayIcon fontSize="small" color={period.calendar_note_id ? 'primary' : 'disabled'} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => { setEditingUnavailability(period); setUnavailabilityDialogOpen(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setDeleteUnavailabilityId(period.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => { setEditingUnavailability(null); setUnavailabilityDialogOpen(true); }}
          size="small"
        >
          {t('booking.config.unavailability_add')}
        </Button>

        {/* ===== Break Periods Subsection ===== */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
          {t('booking.breaks.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.breaks.no_breaks')}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={config.show_breaks_in_calendar || false}
              onChange={(e) => setConfig({ ...config, show_breaks_in_calendar: e.target.checked })}
            />
          }
          label={t('booking.breaks.show_in_calendar')}
          sx={{ mb: 2 }}
        />

        {breakPeriods.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {breakPeriods.map((bp) => (
              <Box
                key={bp.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" fontWeight={500} sx={{ minWidth: 'fit-content' }}>
                  {bp.start_time?.substring(0, 5)} – {bp.end_time?.substring(0, 5)}
                </Typography>
                {bp.name && (
                  <Typography variant="body2" color="text.secondary">
                    ({bp.name})
                  </Typography>
                )}
                {(!bp.days || bp.days.length === 0) ? (
                  <Chip label={t('booking.breaks.all_days')} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {WEEKDAYS.map((day) => (
                      bp.days.includes(day.index) && (
                        <Chip key={day.index} label={day.label} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                      )
                    ))}
                  </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => { setEditingBreak(bp); setBreakDialogOpen(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setDeleteBreakId(bp.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => { setEditingBreak(null); setBreakDialogOpen(true); }}
          size="small"
        >
          {t('booking.breaks.add')}
        </Button>
      </SettingsAccordion>

      {/* ===== Capacity & Timing Accordion ===== */}
      <SettingsAccordion
        id="capacity"
        expanded={!!expanded.capacity}
        onChange={toggleAccordion('capacity')}
        title={t('booking.config.capacity_timing')}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.calendar_aware || false}
                  onChange={(e) => setConfig({ ...config, calendar_aware: e.target.checked })}
                />
              }
              label={t('booking.config.calendar_aware')}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
              {t('booking.config.calendar_aware_help')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('booking.config.concurrent_slots')}
              type="number"
              value={config.concurrent_slots}
              onChange={(e) => setConfig({ ...config, concurrent_slots: parseInt(e.target.value) || 1 })}
              helperText={config.calendar_aware ? t('booking.config.concurrent_slots_fallback_help') : t('booking.config.concurrent_slots_help')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('booking.config.buffer_minutes')}
              type="number"
              value={config.buffer_minutes}
              onChange={(e) => setConfig({ ...config, buffer_minutes: parseInt(e.target.value) || 0 })}
              helperText={t('booking.config.buffer_minutes_help')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('booking.config.max_advance_days')}
              type="number"
              value={config.max_advance_days}
              onChange={(e) => setConfig({ ...config, max_advance_days: parseInt(e.target.value) || 1 })}
              helperText={t('booking.config.max_advance_days_help')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('booking.config.min_advance_hours')}
              type="number"
              value={config.min_advance_hours}
              onChange={(e) => setConfig({ ...config, min_advance_hours: parseInt(e.target.value) || 0 })}
              helperText={t('booking.config.min_advance_hours_help')}
              fullWidth
            />
          </Grid>
        </Grid>
      </SettingsAccordion>

      {/* ===== Reminders & Notifications Accordion (MERGED) ===== */}
      <SettingsAccordion
        id="reminders"
        expanded={!!expanded.reminders}
        onChange={toggleAccordion('reminders')}
        title={t('booking.config.reminders_and_notifications')}
      >
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.reminders')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('booking.config.reminder_hours_before')}
              type="number"
              value={config.reminder_hours_before}
              onChange={(e) => setConfig({ ...config, reminder_hours_before: parseInt(e.target.value) || 0 })}
              helperText={t('booking.config.reminder_hours_before_help')}
              fullWidth
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 3, mb: 1 }}>
          {t('booking.config.notification_channel')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('booking.config.notification_channel_help')}
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            value={config.notification_channel || 'both'}
            onChange={(e) => setConfig({ ...config, notification_channel: e.target.value })}
          >
            <Tooltip title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''} placement="right">
              <FormControlLabel
                value="both"
                control={<Radio disabled={!smsEnabled} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>{t('booking.config.channel_both')}</Typography>
                    {!smsEnabled && isTrial && <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                  </Box>
                }
              />
            </Tooltip>
            <FormControlLabel value="email" control={<Radio />} label={t('booking.config.channel_email')} />
            <Tooltip title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''} placement="right">
              <FormControlLabel
                value="sms"
                control={<Radio disabled={!smsEnabled} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>{t('booking.config.channel_sms')}</Typography>
                    {!smsEnabled && isTrial && <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                  </Box>
                }
              />
            </Tooltip>
          </RadioGroup>
        </FormControl>
        {!smsEnabled && isTrial && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
          </Alert>
        )}
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('booking.config.sms_cost_info')}
        </Alert>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => setPreviewOpen(true)}
          sx={{ mt: 1.5 }}
          data-testid="booking-messages-preview-button"
        >
          {t('booking.config.preview_button')}
        </Button>
        <MessagePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={t('booking.config.preview_title')}
          showLanguage={false}
          loadPreview={async () => {
            // The messages rendered with the channel and confirmation mode on screen, saved or not.
            const res = await axios.post(
              '/api/booking-config/preview/',
              { notification_channel: config.notification_channel, confirmation_mode: config.confirmation_mode },
              { params: { organization: currentOrganization?.organization_id } },
            );
            return res.data;
          }}
        />

        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 3, mb: 2 }}>
          {t('booking.config.notifications')}
        </Typography>
        <TextField
          label={t('booking.config.notification_sms_number')}
          value={config.notification_sms_number}
          onChange={(e) => setConfig({ ...config, notification_sms_number: e.target.value })}
          helperText={t('booking.config.notification_sms_number_help')}
          fullWidth
          placeholder="+358..."
        />
        <TextField
          label={t('booking.config.notification_email')}
          value={config.notification_email || ''}
          onChange={(e) => setConfig({ ...config, notification_email: e.target.value })}
          helperText={t('booking.config.notification_email_help')}
          fullWidth
          placeholder="korjaamo@example.fi"
          type="email"
          sx={{ mt: 2 }}
        />
      </SettingsAccordion>

      {/* ===== Appearance Accordion ===== */}
      <SettingsAccordion
        id="appearance"
        expanded={!!expanded.appearance}
        onChange={toggleAccordion('appearance')}
        title={t('booking.config.appearance')}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('booking.config.appearance_help')}
        </Typography>
        <Grid container spacing={2}>
          {[
            { key: 'button_color', defaultVal: '#2563eb' },
            { key: 'page_bg_color', defaultVal: '#f9fafb' },
            { key: 'card_bg_color', defaultVal: '#ffffff' },
            { key: 'text_color', defaultVal: '#1f2937' },
          ].map(({ key, defaultVal }) => (
            <Grid item xs={12} sm={6} key={key}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {t(`booking.config.${key}`)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  id={`color-swatch-${key}`}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: 'divider',
                    bgcolor: config[key] || defaultVal,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <input
                    ref={(el) => {
                      // Remove old listener if re-assigned
                      if (colorInputRefs.current[key] && colorInputRefs.current[`_handler_${key}`]) {
                        colorInputRefs.current[key].removeEventListener('change', colorInputRefs.current[`_handler_${key}`]);
                      }
                      colorInputRefs.current[key] = el;
                      if (el) {
                        const handler = (e) => setConfig((prev) => ({ ...prev, [key]: e.target.value }));
                        colorInputRefs.current[`_handler_${key}`] = handler;
                        el.addEventListener('change', handler);
                      }
                    }}
                    type="color"
                    defaultValue={config[key] || defaultVal}
                    onInput={(e) => {
                      // Update swatch visually during drag without React re-renders
                      const swatch = document.getElementById(`color-swatch-${key}`);
                      if (swatch) swatch.style.backgroundColor = e.target.value;
                      const label = document.getElementById(`color-label-${key}`);
                      if (label) label.textContent = e.target.value;
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Typography id={`color-label-${key}`} variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {config[key] || defaultVal}
                </Typography>
                {config[key] && (
                  <Button
                    size="small"
                    onClick={() => {
                      setConfig({ ...config, [key]: '' });
                      if (colorInputRefs.current[key]) colorInputRefs.current[key].value = defaultVal;
                      const swatch = document.getElementById(`color-swatch-${key}`);
                      if (swatch) swatch.style.backgroundColor = '';
                      const label = document.getElementById(`color-label-${key}`);
                      if (label) label.textContent = defaultVal;
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    {t('booking.config.reset_color')}
                  </Button>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </SettingsAccordion>

      {/* ===== Custom Texts Accordion ===== */}
      <SettingsAccordion
        id="texts"
        expanded={!!expanded.texts}
        onChange={toggleAccordion('texts')}
        title={t('booking.config.custom_texts')}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('booking.config.custom_welcome_text')}
              value={config.custom_welcome_text}
              onChange={(e) => setConfig({ ...config, custom_welcome_text: e.target.value })}
              helperText={t('booking.config.custom_welcome_text_help')}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('booking.config.custom_confirmation_text')}
              value={config.custom_confirmation_text}
              onChange={(e) => setConfig({ ...config, custom_confirmation_text: e.target.value })}
              helperText={t('booking.config.custom_confirmation_text_help')}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
        </Grid>
      </SettingsAccordion>

      {/* ===== Form Fields Accordion (NEW) ===== */}
      <SettingsAccordion
        id="formFields"
        expanded={!!expanded.formFields}
        onChange={toggleAccordion('formFields')}
        title={t('booking.config.form_fields')}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.form_fields_help')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('booking.config.field_name')}</TableCell>
                <TableCell align="center">{t('booking.config.field_visible')}</TableCell>
                <TableCell align="center">{t('booking.config.field_required')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { key: 'vehicle_registration', label: t('booking.config.field_reg'), help: t('booking.config.field_reg_help') },
                { key: 'customer_name', label: t('booking.config.field_customer_name'), locked: true },
                { key: 'customer_phone', label: t('booking.config.field_phone'), locked: true },
                { key: 'customer_email', label: t('booking.config.field_email') },
                { key: 'customer_notes', label: t('booking.config.field_notes') },
              ].map((field) => {
                const DEFAULT_FIELD = {
                  vehicle_registration: { visible: true, required: true },
                  customer_name: { visible: true, required: true },
                  customer_phone: { visible: true, required: true },
                  customer_email: { visible: true, required: false },
                  customer_notes: { visible: true, required: false },
                };
                const fc = (config.form_field_config || {})[field.key] || DEFAULT_FIELD[field.key] || { visible: true, required: false };
                return (
                  <TableRow key={field.key}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {field.label}
                        {field.help && <HelpButton text={field.help} size={16} />}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={fc.visible !== false}
                        disabled={field.locked}
                        onChange={(e) => {
                          const newConfig = { ...(config.form_field_config || {}), [field.key]: { ...fc, visible: e.target.checked, required: e.target.checked ? fc.required : false } };
                          setConfig({ ...config, form_field_config: newConfig });
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={fc.required === true}
                        disabled={field.locked || fc.visible === false}
                        onChange={(e) => {
                          const newConfig = { ...(config.form_field_config || {}), [field.key]: { ...fc, required: e.target.checked } };
                          setConfig({ ...config, form_field_config: newConfig });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Custom Fields */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            {t('booking.config.custom_fields')}
          </Typography>
          <Button startIcon={<AddIcon />} size="small" onClick={() => { setEditingCustomField(null); setCustomFieldDialogOpen(true); }}>
            {t('booking.config.add_custom_field')}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('booking.config.custom_fields_help')}
        </Typography>
        {(config.custom_form_fields || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('booking.config.no_custom_fields')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('booking.config.field_label')}</TableCell>
                  <TableCell>{t('booking.config.field_type')}</TableCell>
                  <TableCell>{t('booking.config.field_required')}</TableCell>
                  <TableCell align="right">{t('booking.list.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(config.custom_form_fields || []).map((field) => (
                  <TableRow key={field.id}>
                    <TableCell>{field.label}</TableCell>
                    <TableCell>{t('booking.config.field_type_' + field.type)}</TableCell>
                    <TableCell>{field.required ? t('booking.config.yes') : t('booking.config.no')}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { setEditingCustomField(field); setCustomFieldDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => {
                        const updated = (config.custom_form_fields || []).filter(f => f.id !== field.id);
                        setConfig({ ...config, custom_form_fields: updated });
                      }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SettingsAccordion>

      {/* ===== Save Config Button ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSaveConfig}
          disabled={saving}
          size="large"
        >
          {saving ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </Box>

      {/* ===== Service Categories Accordion ===== */}
      <SettingsAccordion
        id="categories"
        expanded={!!expanded.categories}
        onChange={toggleAccordion('categories')}
        title={t('booking.categories.title')}
        badge={categories.length > 0 ? <Chip label={categories.length} size="small" color="primary" /> : null}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => { setEditingCategory(null); setCategoryDialogOpen(true); }}
          >
            {t('booking.categories.add')}
          </Button>
        </Box>

        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('booking.categories.no_categories')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('booking.categories.name')}</TableCell>
                  <TableCell>{t('booking.categories.active')}</TableCell>
                  <TableCell align="right">{t('booking.list.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map((category, idx) => (
                  <TableRow key={category.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{category.name}</Typography>
                      {category.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{category.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={category.is_active}
                        onChange={async (e) => {
                          await axios.patch(`/api/booking-categories/${category.id}/`, { is_active: e.target.checked }, { params: { organization: currentOrganization?.organization_id } });
                          fetchCategories();
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => handleMoveCategory(category, 'up')}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={idx === categories.length - 1}
                        onClick={() => handleMoveCategory(category, 'down')}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setEditingCategory(category); setCategoryDialogOpen(true); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteCategoryId(category.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SettingsAccordion>

      {/* ===== Bookable Services Accordion ===== */}
      <SettingsAccordion
        id="services"
        expanded={!!expanded.services}
        onChange={toggleAccordion('services')}
        title={t('booking.services.title')}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => { setEditingService(null); setServiceDialogOpen(true); }}
          >
            {t('booking.services.add')}
          </Button>
        </Box>

        {services.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('booking.services.no_services')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('booking.services.name')}</TableCell>
                  <TableCell>{t('booking.services.duration')}</TableCell>
                  <TableCell>{t('booking.services.price')}</TableCell>
                  <TableCell>{t('booking.services.mode')}</TableCell>
                  <TableCell>{t('booking.services.active')}</TableCell>
                  <TableCell align="right">{t('booking.list.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...services].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)).map((service, idx) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{service.name}</Typography>
                      {categories.find(c => c.id === service.category)?.name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{categories.find(c => c.id === service.category)?.name}</Typography>
                      )}
                      {service.job_name && (
                        <Chip
                          label={`${service.job_name}${service.job_price_incl_vat != null ? ` (${parseFloat(service.job_price_incl_vat).toFixed(2)} €)` : ''}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{service.duration_minutes} min</TableCell>
                    <TableCell>{service.price_indication || '–'}</TableCell>
                    <TableCell>
                      {service.booking_mode === 'time_slot'
                        ? t('booking.services.mode_time_slot')
                        : t('booking.services.mode_drop_off')
                      }
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={service.is_active}
                        onChange={async (e) => {
                          await axios.patch(`/api/booking-services/${service.id}/`, { is_active: e.target.checked });
                          fetchServices();
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => handleMoveService(service, 'up')}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={idx === services.length - 1}
                        onClick={() => handleMoveService(service, 'down')}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => { setEditingService(service); setServiceDialogOpen(true); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteServiceId(service.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SettingsAccordion>

      {/* ===== Extra Services Accordion ===== */}
      <SettingsAccordion
        id="extras"
        expanded={!!expanded.extras}
        onChange={toggleAccordion('extras')}
        title={t('booking.extras.title')}
        badge={extras.length > 0 ? <Chip label={extras.length} size="small" color="primary" /> : null}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={() => { setEditingExtra(null); setExtraDialogOpen(true); }}
          >
            {t('booking.extras.add')}
          </Button>
        </Box>

        {extras.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('booking.extras.no_extras')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('booking.extras.name')}</TableCell>
                  <TableCell>{t('booking.extras.price')}</TableCell>
                  <TableCell>{t('booking.extras.duration')}</TableCell>
                  <TableCell>{t('booking.extras.services_linked')}</TableCell>
                  <TableCell>{t('booking.extras.active')}</TableCell>
                  <TableCell align="right">{t('booking.list.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {extras.map((extra) => (
                  <TableRow key={extra.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{extra.name}</Typography>
                      {extra.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{extra.description}</Typography>
                      )}
                      {extra.job_name && (
                        <Chip
                          label={`${extra.job_name}${extra.job_price_incl_vat != null ? ` (${parseFloat(extra.job_price_incl_vat).toFixed(2)} €)` : ''}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{extra.price_indication || '–'}</TableCell>
                    <TableCell>{extra.duration_minutes ? extra.duration_minutes + ' min' : '–'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {extra.services?.map((svcId) => {
                          const svc = services.find((s) => s.id === svcId);
                          return svc ? (
                            <Chip key={svcId} label={svc.name} size="small" variant="outlined" />
                          ) : null;
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={extra.is_active}
                        onChange={async (e) => {
                          await axios.patch(`/api/booking-extras/${extra.id}/`, { is_active: e.target.checked });
                          fetchExtras();
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => { setEditingExtra(extra); setExtraDialogOpen(true); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteExtraId(extra.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SettingsAccordion>

      {/* ===== Drop-off Windows Accordion (only if any drop-off services exist) ===== */}
      {hasDropOffServices && (
        <SettingsAccordion
          id="dropoff"
          expanded={!!expanded.dropoff}
          onChange={toggleAccordion('dropoff')}
          title={t('booking.drop_off.title')}
          badge={dropOffWindows.length > 0 ? <Chip label={dropOffWindows.length} size="small" color="primary" /> : null}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() => { setEditingWindow(null); setWindowDialogOpen(true); }}
            >
              {t('booking.drop_off.add')}
            </Button>
          </Box>

          {dropOffWindows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('booking.drop_off.no_windows')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('booking.drop_off.name')}</TableCell>
                    <TableCell>{t('booking.drop_off.start_time')}</TableCell>
                    <TableCell>{t('booking.drop_off.end_time')}</TableCell>
                    <TableCell>{t('booking.drop_off.capacity')}</TableCell>
                    <TableCell>{t('booking.drop_off.available_days')}</TableCell>
                    <TableCell align="right">{t('booking.list.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dropOffWindows.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.name}</TableCell>
                      <TableCell>{w.start_time?.substring(0, 5)}</TableCell>
                      <TableCell>{w.end_time?.substring(0, 5)}</TableCell>
                      <TableCell>{w.daily_capacity}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {WEEKDAYS.map((day) => (
                            w.available_days?.includes(day.index) && (
                              <Chip key={day.index} label={day.label} size="small" variant="outlined" />
                            )
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => { setEditingWindow(w); setWindowDialogOpen(true); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteWindowId(w.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SettingsAccordion>
      )}

      {/* ===== Dialogs ===== */}
      <ServiceCategoryDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        onSave={handleSaveCategory}
        category={editingCategory}
        t={t}
      />

      <ServiceDialog
        open={serviceDialogOpen}
        onClose={() => setServiceDialogOpen(false)}
        onSave={handleSaveService}
        service={editingService}
        calendarAware={config?.calendar_aware}
        availableEmployees={orgEmployees}
        availableBays={orgServiceBays}
        categories={categories}
        t={t}
      />

      <ExtraDialog
        open={extraDialogOpen}
        onClose={() => setExtraDialogOpen(false)}
        onSave={handleSaveExtra}
        extra={editingExtra}
        services={services}
        t={t}
      />

      <DropOffWindowDialog
        open={windowDialogOpen}
        onClose={() => setWindowDialogOpen(false)}
        onSave={handleSaveWindow}
        window={editingWindow}
        t={t}
      />

      <UnavailabilityDialog
        open={unavailabilityDialogOpen}
        onClose={() => setUnavailabilityDialogOpen(false)}
        onSave={handleSaveUnavailability}
        period={editingUnavailability}
        t={t}
      />

      <BreakPeriodDialog
        open={breakDialogOpen}
        onClose={() => setBreakDialogOpen(false)}
        onSave={handleSaveBreak}
        breakPeriod={editingBreak}
        t={t}
      />

      <CustomFieldDialog
        open={customFieldDialogOpen}
        onClose={() => setCustomFieldDialogOpen(false)}
        onSave={handleSaveCustomField}
        field={editingCustomField}
        t={t}
      />

      {/* Delete Category Confirmation */}
      <Dialog open={!!deleteCategoryId} onClose={() => setDeleteCategoryId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.categories.delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteCategory} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Service Confirmation */}
      <Dialog open={!!deleteServiceId} onClose={() => setDeleteServiceId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.services.delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteServiceId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteService} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Extra Confirmation */}
      <Dialog open={!!deleteExtraId} onClose={() => setDeleteExtraId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.extras.delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteExtraId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteExtra} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Window Confirmation */}
      <Dialog open={!!deleteWindowId} onClose={() => setDeleteWindowId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.drop_off.delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteWindowId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteWindow} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Unavailability Period Confirmation */}
      <Dialog open={!!deleteUnavailabilityId} onClose={() => setDeleteUnavailabilityId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.config.unavailability_delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUnavailabilityId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteUnavailability} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Break Period Confirmation */}
      <Dialog open={!!deleteBreakId} onClose={() => setDeleteBreakId(null)}>
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('booking.breaks.delete_confirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBreakId(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDeleteBreak} color="error" variant="contained">{t('Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
